import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from 'common/services/api'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import {
  CURRENT_STOCK_QUERY_KEY,
  DISPOSAL_URL,
  STOCK_LOGS_QUERY_KEY
} from './queryConstants'
import { DisposalData, MedicationStockReason } from '../types'
import { useUpdateStockRecord } from './useUpdateStockRecord'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'

type UseDisposalProps = {
  residentId: string
  residentMedicationId: string
  medicationName?: string
  onSuccess?: () => void
  onError?: (error: any) => void
}

type DisposalRequest = {
  residentId: string
  residentMedicationId: string
  actionTime?: string
  quantity: number
  witnessIds: string[]
  notes: string
  disposalMethod: string
  disposedBy?: { id: string }
  mediaAttachments: any[]
  discrepancyReason?: string
}

export const useDisposal = ({
  residentId,
  residentMedicationId,
  medicationName,
  onSuccess,
  onError
}: UseDisposalProps) => {
  const queryClient = useQueryClient()
  const sendEvent = useMixpanelEvent()

  const { updateStockRecord, isLoading: isUpdating } = useUpdateStockRecord({
    onSuccess,
    onError
  })

  const createMutation = useMutation(
    (data: DisposalData) => {
      const actionTimeISO =
        data.actionTime &&
        typeof data.actionTime.isValid === 'function' &&
        data.actionTime.isValid()
          ? dayjs
              .utc(data.actionTime.format('YYYY-MM-DD'))
              .startOf('day')
              .toISOString()
          : undefined

      const hasMedia = data.media && data.media.length > 0

      if (hasMedia) {
        const formData = new FormData()

        formData.append('residentId', residentId)
        formData.append('residentMedicationId', residentMedicationId)
        if (actionTimeISO !== undefined) {
          formData.append('actionTime', actionTimeISO)
        }
        formData.append('quantity', String(data.disposedStock))
        formData.append('notes', data.description)
        formData.append('disposalMethod', data.disposalMethod)
        if (data.actionedBy) {
          formData.append('disposedBy[id]', data.actionedBy)
        }
        if (data.hasDiscrepancy && data.discrepancyReason) {
          formData.append('discrepancyReason', data.discrepancyReason)
        }

        data.media?.forEach((file: File | undefined) => {
          if (file) {
            formData.append('media', file)
          }
        })

        const url = `${DISPOSAL_URL}?witnessCheckRequired=false`

        return postJSON({
          url,
          contentType: 'multipart/form-data',
          body: formData
        })
      }

      const requestBody: DisposalRequest = {
        residentId,
        residentMedicationId,
        ...(actionTimeISO !== undefined && { actionTime: actionTimeISO }),
        quantity: data.disposedStock,
        witnessIds: [],
        notes: data.description,
        disposalMethod: data.disposalMethod,
        disposedBy: data.actionedBy ? { id: data.actionedBy } : undefined,
        mediaAttachments: [],
        discrepancyReason:
          data.hasDiscrepancy && data.discrepancyReason
            ? data.discrepancyReason
            : undefined
      }

      const url = `${DISPOSAL_URL}?witnessCheckRequired=false`

      return postJSON({
        url,
        json: requestBody
      })
    },
    {
      onSuccess: (_, variables) => {
        if (medicationName) {
          sendEvent(MixpanelEvents.EMAR_V2_STOCK_RECORD_CREATED, {
            medicationName,
            typeOfRecord: 'Disposal',
            time: dayjs().toISOString(),
            discrepanciesFlagged: variables.hasDiscrepancy || false,
            discrepanciesClosed: false
          })
        }

        queryClient.invalidateQueries([STOCK_LOGS_QUERY_KEY])
        queryClient.invalidateQueries([CURRENT_STOCK_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error) => {
        console.error('Error saving disposal:', error)
        onError?.(error)
      }
    }
  )

  const updateDisposal = async ({
    recordId,
    data
  }: {
    recordId: string
    data: DisposalData
  }) => {
    let actionTime: string | undefined

    if (
      data.actionTime &&
      typeof data.actionTime.isValid === 'function' &&
      data.actionTime.isValid()
    ) {
      actionTime = dayjs
        .utc(data.actionTime.format('YYYY-MM-DD'))
        .startOf('day')
        .toISOString()
    }

    return updateStockRecord({
      recordId,
      type: MedicationStockReason.DISPOSAL,
      data: {
        notes: data.description,
        witnessIds: [],
        disposedBy: data.actionedBy || undefined,
        disposalMethod: data.disposalMethod || undefined,
        actionTime,
        discrepancyReason:
          data.hasDiscrepancy && data.discrepancyReason
            ? data.discrepancyReason
            : undefined,
        media: data.media,
        mediaAttachmentKeysToRemove: data.mediaAttachmentKeysToRemove
      }
    })
  }

  return {
    createDisposal: createMutation.mutateAsync,
    updateDisposal,
    isLoading: createMutation.isLoading || isUpdating
  }
}

export default useDisposal
